import { filterGeneratedChunks } from '../../core/content/filterGeneratedLinks';
import { newId } from '../../core/ids';
import { getFeatureFlags, loadFeatureFlags } from '../../core/featureFlags';
import { SERVER_PROVIDER_ID } from '../../core/providers';
import type { ProviderAdapter } from '../../core/providers';
import { conversationRepo, messageRepo, presetRepo, providerRepo } from '../../core/repositories';
import { loadModelSettings } from '../../core/settings';
import { resolveRoleConversation, validateRoleModelLimits } from '../../core/roleModel';
import { applyRoleContextBudget } from '../../core/roleContext';
import { buildIntimacyContext } from '../intimacy/client';
import type { ChatMessage, Conversation, Preset, ProviderConfig } from '../../core/types';
import { assembleChatMessages } from '../chat/contextAssembly';
import { getActiveConversationId } from '../chat/activeConversation';
import { registerChatRun } from '../chat/backgroundChat';
import { readSecret } from '../providers/secretStore';
import { loadOfficialCatalog } from '../chat/officialModelCatalog';
import { ensureFreshEntitlements, getEntitlements } from '../subscription/entitlements';
import { claimDueAutomation, finishAutomation, rescheduleAutomation } from './eventRepo';
import {
  automationAllowsBubble,
  applyControlToolCalls,
  buildAutomationInstructions,
  buildRoleTools,
  completedToolCalls,
  mergeToolCallDelta,
  parseRichOutput,
} from './richOutput';
import type { CompletedToolCall, StreamToolCall } from './richOutput';
import { inQuietPeriod, shiftOutOfQuietPeriod } from './quietHours';
import { applyRichOutput, roleImageAvailable } from './media';
import { modelGateway } from '../modelGateway';
import { decideRoleVoice } from './voiceDecision';

const MAX_OVERDUE_MS = 24 * 60 * 60_000;
const SERVER_PROVIDER_CONFIG = {
  id: SERVER_PROVIDER_ID,
  kind: 'custom' as const,
  name: '官方模型',
  baseUrl: '',
  apiKeyRef: '',
  enabled: true,
};
let running = false;

function officialModelId(value: string): string {
  const matches = [...value.matchAll(/\(([^()]+)\)/g)];
  return matches[matches.length - 1]?.[1] ?? value;
}

function adapterFor(conversation: Conversation, providers: readonly ProviderConfig[]): { adapter: ProviderAdapter; model: string } | null {
  const model = (conversation.model ?? '').trim();
  if (model === '') return null;
  if (conversation.providerId === SERVER_PROVIDER_ID) {
    return { adapter: modelGateway.createChatAdapter(SERVER_PROVIDER_CONFIG), model: officialModelId(model) };
  }
  const provider = providers.find((row) => row.id === conversation.providerId && row.enabled);
  if (!provider || provider.purpose === 'image' || provider.purpose === 'tts') return null;
  const apiKey = provider.apiKeyRef.trim() ? readSecret(provider.apiKeyRef) : null;
  return { adapter: modelGateway.createChatAdapter(apiKey === null ? provider : { ...provider, apiKey }), model };
}

function latestConversation(conversations: readonly Conversation[], presetId: string): Conversation | null {
  return [...conversations]
    .filter((conversation) => conversation.presetId === presetId)
    .sort((a, b) => b.updatedAt - a.updatedAt)[0] ?? null;
}

function withTaskInstruction(
  messages: ReturnType<typeof assembleChatMessages>,
  preset: Preset,
  task: string,
): void {
  if (messages.systemMessage) {
    messages.systemMessage.content = `${messages.systemMessage.content}\n\n${task}`;
    const first = messages.messages[0];
    if (first?.role === 'system') messages.messages[0] = { ...first, content: messages.systemMessage.content };
  } else {
    const systemMessage: ChatMessage = {
      id: newId(),
      conversationId: messages.messages[0]?.conversationId ?? 'automation',
      role: 'system',
      content: task,
      createdAt: Date.now(),
      status: 'done',
    };
    messages.messages.unshift(systemMessage);
    messages.systemMessage = systemMessage;
  }
  void preset;
}

async function generate(
  preset: Preset,
  conversation: Conversation,
  history: readonly ChatMessage[],
  providers: readonly ProviderConfig[],
  kind: 'bubble' | 'moment',
): Promise<{ raw: string; calls: CompletedToolCall[] }> {
  const target = adapterFor(conversation, providers);
  if (!target) throw new Error('联系人的聊天模型不可用');
  const entitlements = getEntitlements();
  const officialImageOptions = entitlements.active && entitlements.capabilities.autoImage && preset.autoImageEnabled
    ? (await loadOfficialCatalog().catch(() => ({ models: [], image: [] }))).image : [];
  const autoImage = entitlements.active && entitlements.capabilities.autoImage
    && roleImageAvailable(preset, { providers, officialImageOptions });
  const assembled = assembleChatMessages({
    conversationId: conversation.id,
    messages: [...history],
    preset,
  });
  const features = await loadFeatureFlags(true);
  const task = kind === 'bubble' ? features.automationPrompts.bubbleTask : features.automationPrompts.momentTask;
  const instructions = buildAutomationInstructions(preset, {
    autoText: entitlements.active && entitlements.capabilities.autoText,
    autoImage,
    autoTts: entitlements.active && entitlements.capabilities.autoTts,
    allowSchedules: false,
  }, features);
  withTaskInstruction(assembled, preset, [instructions, task].filter(Boolean).join('\n\n'));
  if (features.automationPrompts.taskExecution.trim() !== '') assembled.messages.push({
    id: newId(),
    conversationId: conversation.id,
    role: 'user',
    content: features.automationPrompts.taskExecution,
    createdAt: Date.now(),
    status: 'done',
  });
  const settings = conversation.settings ?? loadModelSettings();
  let raw = '';
  let failure: string | null = null;
  const toolDeltas = new Map<number, StreamToolCall>();
  const tools = buildRoleTools(preset, {
    autoText: entitlements.active && entitlements.capabilities.autoText,
    autoImage,
    autoTts: entitlements.active && entitlements.capabilities.autoTts,
    allowSchedules: false,
  }, features);
  const intimacy = await buildIntimacyContext(preset.id);
  for await (const chunk of filterGeneratedChunks(target.adapter.chat(applyRoleContextBudget({
    messages: assembled.messages,
    settings: assembled.systemMessage ? { ...settings, systemPrompt: undefined } : settings,
    model: target.model,
    ...(tools.length > 0 ? { tools } : {}),
    ...(intimacy ? { intimacy: { ...intimacy, activity: 'automation' as const } } : {}),
  }, preset)))) {
    if (chunk.type === 'text') raw += chunk.delta ?? '';
    else if (chunk.type === 'tool_call') mergeToolCallDelta(toolDeltas, chunk.delta ?? '');
    else if (chunk.type === 'error') {
      failure = chunk.error ?? '自动生成失败';
      break;
    } else if (chunk.type === 'done') break;
  }
  if (failure) throw new Error(failure);
  const calls = completedToolCalls(toolDeltas);
  const text = applyControlToolCalls(parseRichOutput(raw), calls).text.trim();
  if (text === '') throw new Error('自动生成内容为空');
  return { raw, calls };
}

async function executeEvent(event: NonNullable<Awaited<ReturnType<typeof claimDueAutomation>>>): Promise<void> {
  if (event.kind !== 'bubble') return;
  if (Date.now() - event.dueAt > MAX_OVERDUE_MS) {
    await finishAutomation(event.id, 'skipped', '超过 24 小时未执行');
    return;
  }
  await ensureFreshEntitlements(30_000);
  const entitlements = getEntitlements();
  if (!entitlements.active || !entitlements.capabilities.autoText) {
    await finishAutomation(event.id, 'skipped', '当前套餐不支持自动生文');
    return;
  }
  const [presets, conversations, providers] = await Promise.all([
    presetRepo.listPresets(),
    conversationRepo.listConversations(),
    providerRepo.listProviders(),
  ]);
  const preset = presets.find((row) => row.id === event.presetId);
  if (!preset) {
    await finishAutomation(event.id, 'skipped', '联系人不存在');
    return;
  }
  if (event.kind === 'bubble' && !automationAllowsBubble(preset.automationMode)) {
    await finishAutomation(event.id, 'skipped', '自动化模式已关闭冒泡');
    return;
  }
  if (event.kind === 'bubble' && inQuietPeriod(new Date(), preset.quietPeriods ?? [])) {
    await rescheduleAutomation(event.id, shiftOutOfQuietPeriod(new Date(), preset.quietPeriods ?? []).getTime());
    return;
  }
  const previous = latestConversation(preset.modelConfig === undefined ? conversations : conversations.filter(row => row.modelKind !== 'image'), preset.id);
  let conversation = resolveRoleConversation(preset, previous ?? undefined, providers);
  if (!conversation) {
    await finishAutomation(event.id, 'skipped', '联系人还没有会话');
    return;
  }
  if (preset.modelConfig !== undefined && conversation.providerId === SERVER_PROVIDER_ID) {
    const option = (await loadOfficialCatalog()).chat.find(row => row.slug === conversation!.model || row.label === conversation!.model);
    if (!option) throw new Error('角色官方文字模型已下架或不可用，请重新选择角色模型');
    validateRoleModelLimits(preset.modelConfig, option);
  }
  if (!previous) {
    conversation = await conversationRepo.createAndSaveConversation({ title: preset.name, presetId: preset.id,
      providerId: conversation.providerId, model: conversation.model, modelKind: 'text', settings: conversation.settings });
  }
  const history = (await messageRepo.listMessages(conversation.id)).slice(-20);
  const generated = await generate(preset, conversation, history, providers, event.kind);
  const parsed = applyControlToolCalls(parseRichOutput(generated.raw), generated.calls);
  parsed.bubbleDelayMinutes = null;
  parsed.momentDelayMinutes = null;
  parsed.momentCheckDelayMinutes = null;
  parsed.momentPublications = [];

  if (event.kind === 'bubble') {
    const activeHere = window.location.hash.startsWith('#/chat') && getActiveConversationId() === conversation.id;
    const messageId = newId();
    const controller = new AbortController();
    const releaseRun = registerChatRun(messageId, controller);
    try {
      let message = await messageRepo.createAndSaveMessage({
        id: messageId,
        conversationId: conversation.id,
        role: 'assistant',
        content: parsed.text,
        status: 'done',
        readAt: activeHere ? Date.now() : 0,
        roleVoicePending: preset.autoTtsEnabled === true && entitlements.capabilities.autoTts,
      });
      if (!parsed.ttsRequested && preset.autoTtsEnabled && entitlements.capabilities.autoTts) {
        try {
          const target = adapterFor(conversation, providers);
          if (!target) throw new Error('联系人的聊天模型不可用');
          const context = assembleChatMessages({ conversationId: conversation.id, messages: [...history, message], preset });
          parsed.ttsRequested = await decideRoleVoice({
            preset, message, context: context.messages, adapter: target.adapter, model: target.model,
            settings: { ...(conversation.settings ?? loadModelSettings()), systemPrompt: undefined }, features: getFeatureFlags(),
            signal: controller.signal,
          });
          message = await messageRepo.updateMessage(message.id, { roleVoiceRequested: parsed.ttsRequested,
            ...(!parsed.ttsRequested ? { roleVoicePending: false } : {}) }) ?? message;
        } catch (error) {
          message = await messageRepo.updateMessage(message.id, { audioError: error instanceof Error ? error.message : '语音发送决策失败' }) ?? message;
        }
      }
      const rich = await applyRichOutput(preset, message, parsed, {
        providers,
        signal: controller.signal,
        officialImageOptions: (await loadOfficialCatalog().catch(() => ({ models: [], image: [] }))).image,
      });
      if (rich.message) message = rich.message;
      const count = await messageRepo.countMessages(conversation.id);
      await conversationRepo.updateConversation(conversation.id, { messageCount: count, updatedAt: Date.now() });
    } finally {
      try { await messageRepo.updateMessage(messageId, { roleVoicePending: false }); }
      finally { releaseRun(); }
    }
  }
  await finishAutomation(event.id, 'done');
}

export async function runDueAutomation(): Promise<void> {
  if (running) return;
  running = true;
  try {
    for (let index = 0; index < 5; index += 1) {
      const event = await claimDueAutomation();
      if (!event) break;
      try {
        await executeEvent(event);
      } catch (error) {
        await finishAutomation(event.id, 'error', error instanceof Error ? error.message.slice(0, 500) : '自动任务失败');
      }
    }
  } finally {
    running = false;
  }
}
